import * as React from 'react';
import toast from 'react-hot-toast';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  LoadingState,
  Page,
  PageHeader,
  PageSection,
  Select,
  Table,
} from '@/components';
import { useContacts, useUpdateContact } from '@/hooks';

export function Contacts() {
  const [search, setSearch] = React.useState('');
  const [optIn, setOptIn] = React.useState<'all' | 'true' | 'false'>('all');
  const [page, setPage] = React.useState(1);
  const limit = 20;

  const query = useContacts({
    search: search.trim() || undefined,
    opt_in: optIn === 'all' ? undefined : optIn === 'true',
    page,
    limit,
  });

  const update = useUpdateContact();

  const toggleOptIn = async (id: string, current: boolean) => {
    try {
      await update.mutateAsync({ id, payload: { opt_in: !current } });
      toast.success('Contato atualizado');
      await query.refetch();
    } catch {
      toast.error('Não foi possível atualizar o contato');
    }
  };

  const data = query.data;

  return (
    <Page>
      <PageHeader
        title="Contatos"
        description="Lista de contatos salvos no Supabase (opt-in, tags e metadados)."
        right={
          <Button variant="secondary" onClick={() => query.refetch()} disabled={query.isFetching}>
            Recarregar
          </Button>
        }
      />

      <PageSection>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="font-semibold">Filtros</div>
              <Badge variant="neutral">Supabase</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flow-grid-2">
              <FormField label="Buscar">
                <Input
                  value={search}
                  placeholder="Nome, telefone, email..."
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </FormField>

              <FormField label="Opt-in">
                <Select
                  value={optIn}
                  onChange={(e) => {
                    setOptIn(e.target.value as any);
                    setPage(1);
                  }}
                >
                  <option value="all">Todos</option>
                  <option value="true">Somente opt-in</option>
                  <option value="false">Somente opt-out</option>
                </Select>
              </FormField>
            </div>
          </CardBody>
        </Card>
      </PageSection>

      <PageSection>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="font-semibold">Lista</div>
              <div className="text-sm text-gray-600">
                {data?.pagination?.total != null ? `${data.pagination.total} contatos` : ''}
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {query.isLoading ? (
              <LoadingState title="Carregando contatos" description="Buscando contatos no Supabase." />
            ) : query.isError ? (
              <ErrorState title="Falha ao carregar contatos" onRetry={() => query.refetch()} />
            ) : !data?.data?.length ? (
              <EmptyState title="Nenhum contato encontrado" description="Ajuste filtros ou cadastre contatos no Supabase." />
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Telefone</th>
                      <th>Tags</th>
                      <th>Opt-in</th>
                      <th>Atualizado</th>
                      <th style={{ width: 160 }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((c) => (
                      <tr key={c.id}>
                        <td className="font-medium">{c.name ?? '-'}</td>
                        <td className="font-medium">{c.phone}</td>
                        <td className="text-gray-500">{(c.tags ?? []).join(', ') || '-'}</td>
                        <td>
                          <Badge variant={c.opt_in ? 'success' : 'neutral'}>{c.opt_in ? 'true' : 'false'}</Badge>
                        </td>
                        <td className="text-gray-500">{c.updated_at?.slice(0, 10) ?? '-'}</td>
                        <td>
                          <div className="flex items-center" style={{ gap: 8 }}>
                            <Button
                              variant="secondary"
                              onClick={() => {
                                try {
                                  navigator.clipboard.writeText(c.phone);
                                  toast.success('Telefone copiado');
                                } catch {
                                  toast.error('Não foi possível copiar');
                                }
                              }}
                            >
                              Copiar
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => toggleOptIn(c.id, c.opt_in)}
                              disabled={update.isLoading}
                            >
                              {c.opt_in ? 'Opt-out' : 'Opt-in'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
                  <div className="text-sm text-gray-600">
                    Página {data.pagination.page} de {data.pagination.pages}
                  </div>
                  <div className="flex items-center" style={{ gap: 8 }}>
                    <Button variant="secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                      Anterior
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setPage((p) => Math.min(data.pagination.pages, p + 1))}
                      disabled={page >= data.pagination.pages}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </PageSection>
    </Page>
  );
}
